import React, { useCallback, useMemo, useState } from 'react'
import { TrackingUser } from '@tabeeb/contracts/main-api/swagger-main-api'
import {
  DataGridProProps,
  GridFilterItem,
  GridFilterModel,
  GridToolbarContainer,
  GridToolbarFilterButton,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import useCheckInsTableColumns from '@tabeeb/modules/checkIns/components/CheckInsTable/useCheckInsTableColumns'
import ImageViewer, { ImageViewerProps } from '@collaborate-health/uikit/components/ImageViewer'
import { DataGridV2, PaginationProps, Stack, useDialogState } from '@collaborate-health/uikit'
import RemoveUserRecordsDialog from '@tabeeb/modules/checkIns/components/RemoveUserRecordsDialog'
import { useDispatch, useSelector } from '@tabeeb/store'
import { getContentId } from '@tabeeb/shared/content/selectors'
import { getCheckInsFilterModel, setCheckInsFilterModel } from '@tabeeb/modules/checkIns'
import { CheckInRow, CheckInsTableProps } from './CheckInsTable.types'
import { getRowId, getTreeDataPath, hasExpandableChildren } from './CheckInsTable.helpers'

const CheckInsTable: React.FC<CheckInsTableProps> = ({
  checkIns,
  pageSize,
  totalCount,
  viewType,
  onNextPage,
  setPageSize,
  setFilters,
}) => {
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState<number>(0)
  const filterModel = useSelector(getCheckInsFilterModel)
  const [removeUserRecordsDialogOpen, onRemoveUserRecordsDialogOpen, onRemoveUserRecordsDialogClose] = useDialogState()
  const [selectedUser, setSelectedUser] = useState<TrackingUser | null>(null)
  const contentId: number = useSelector(getContentId)
  const apiRef = useGridApiRef()

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page)
      if (page > currentPage && checkIns.pages.length <= page) {
        onNextPage()
      }
    },
    [checkIns.pages.length, currentPage, onNextPage]
  )

  const currentPageItems = useMemo(() => {
    if (checkIns.pages.length > currentPage) {
      return checkIns.pages[currentPage].Users ?? []
    }
    return []
  }, [checkIns, currentPage])

  const [imageViewerSlides, setImageViewerSlides] = useState<ImageViewerProps['slides']>([])
  const [imageViewerOpen, onOpenImageViewer, onCloseImageViewer] = useDialogState()

  const handleOpenImageViewer = useCallback(
    (url: string) => {
      setImageViewerSlides([{ src: url, download: { url, filename: '' } }])
      onOpenImageViewer()
    },
    [onOpenImageViewer]
  )

  const handleRemoveUserRecord = useCallback(
    (user: TrackingUser) => {
      setSelectedUser(user)
      onRemoveUserRecordsDialogOpen()
    },
    [onRemoveUserRecordsDialogOpen]
  )

  const rows = useMemo<CheckInRow[]>(() => {
    const treeRows: CheckInRow[] = []

    currentPageItems.forEach((user) => {
      treeRows.push({ ...user, isRoot: true })

      if (!hasExpandableChildren(user)) {
        return
      }

      const qualifications = user.Qualifications || []
      const childRows: CheckInRow[] = []

      qualifications.forEach((qual, qualIndex) => {
        const certificates = qual.Certificates || []

        if (certificates.length === 0) {
          if (qualIndex > 0 || qualifications[0].Certificates?.length) {
            childRows.push({
              ...user,
              qualificationIndex: qualIndex,
              qualification: qual,
              isFirstCertOfQual: true,
            })
          }
        } else {
          certificates.forEach((cert, certIndex) => {
            if (qualIndex === 0 && certIndex === 0) {
              return
            }
            childRows.push({
              ...user,
              qualificationIndex: qualIndex,
              certificateIndex: certIndex,
              qualification: qual,
              certificate: cert,
              isFirstCertOfQual: certIndex === 0,
            })
          })
        }
      })

      treeRows.push(...childRows)
    })

    return treeRows
  }, [currentPageItems])

  const columns = useCheckInsTableColumns({
    checkIns,
    viewType,
    onRemoveUserRecord: handleRemoveUserRecord,
    onOpenImageViewer: handleOpenImageViewer,
  })

  const toolbar = useCallback(
    () => (
      <GridToolbarContainer>
        <GridToolbarFilterButton />
      </GridToolbarContainer>
    ),
    []
  )

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      const configuredItems = model.items.filter((item) => item.field && item.value)

      const uniqueConfiguredItems = configuredItems.reduce<GridFilterItem[]>((acc, item) => {
        if (!acc.some((i) => i.field === item.field)) {
          acc.push(item)
        }
        return acc
      }, [])

      const newFilterModel: GridFilterModel = {
        ...model,
        items: uniqueConfiguredItems,
      }

      dispatch(setCheckInsFilterModel(newFilterModel))

      setCurrentPage(0)

      const dict = uniqueConfiguredItems.reduce<Record<string, GridFilterItem['value']>>((acc, item) => {
        if (item.field) {
          acc[item.field] = item.value
        }
        return acc
      }, {})

      setFilters({
        Name: dict.name ?? null,
        QualificationName: dict.qualifications ?? null,
        CertificateTypeName: dict.certificateType ?? null,
        CertificateStatus: dict.certificateStatus ?? null,
        QualificationStatus: dict.qualificationStatus ?? null,
      })
    },
    [dispatch, setFilters]
  )

  const totalPages = useMemo(() => Math.ceil(totalCount / pageSize), [totalCount, pageSize])

  const paginationState = useMemo((): PaginationProps => {
    return {
      current: currentPage + 1,
      pages: totalPages,
      pageSize,
      pageSizeOptions: [10, 15, 25, 50, 100],
      onPageNumberChange: (pageNumber: number) => handlePageChange(pageNumber - 1),
      onPageSizeChange: setPageSize,
    }
  }, [currentPage, totalPages, pageSize, handlePageChange, setPageSize])

  const groupingColDef = useMemo(
    (): DataGridProProps['groupingColDef'] => ({
      headerName: '',
      maxWidth: 30,
      valueFormatter: () => null,
    }),
    []
  )

  return (
    <Stack width='100%' height='100%' overflow='auto'>
      <DataGridV2
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        treeData
        getTreeDataPath={getTreeDataPath}
        groupingColDef={groupingColDef}
        rowHeight={70}
        columnHeaderHeight={30}
        disableColumnFilter={false}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        rowCount={totalCount}
        paginationState={paginationState}
        disableRowSelectionOnClick
        slots={{
          toolbar,
        }}
        slotProps={{
          filterPanel: {
            logicOperators: [],
          },
        }}
      />
      {selectedUser && (
        <RemoveUserRecordsDialog
          open={removeUserRecordsDialogOpen}
          onClose={onRemoveUserRecordsDialogClose}
          user={selectedUser}
          contentId={contentId}
        />
      )}
      <ImageViewer open={imageViewerOpen} onClose={onCloseImageViewer} slides={imageViewerSlides} />
    </Stack>
  )
}

export default CheckInsTable

